import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { sgo } from "@/lib/api";
import { Users, Calculator, Settings, ArrowLeft, LogOut, FileSpreadsheet } from "lucide-react";

/**
 * Layout compartilhado do SGO — header institucional com KPIs + sidebar de módulos
 * Usado por CadastroClientes e Orçamento
 */
export function SgoShell({ children, activeModule = "clientes" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dash, setDash] = useState({ nf_total: 0, lucro: 0, liquido: 0, postos: 0, cct_ativa: null, dias_vigencia: null });

  useEffect(() => {
    sgo.dashboard().then(r => setDash(r.data)).catch(() => {});
  }, []);

  const fmt = (v) => (v ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const cctInfo = dash.cct_ativa
    ? `${dash.cct_ativa.nome} · ${dash.cct_ativa.sindicato_patronal || dash.cct_ativa.sindicato} · ${dash.cct_ativa.uf}`
    : "Nenhuma CCT vigente";

  return (
    <div className="min-h-screen bg-[#0A0B1A] flex flex-col">
      {/* HEADER institucional com KPIs */}
      <header className="bg-gradient-to-r from-[#1A1D42] to-[#252952] border-b border-[#B28F4B]/25 sticky top-0 z-20 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
        <div className="max-w-[1600px] mx-auto px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Logo + CCT ativa */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-[#0A0B1A] border-2 border-[#B28F4B] flex items-center justify-center gold-glow shrink-0">
              <FileSpreadsheet size={22} className="text-[#B28F4B]"/>
            </div>
            <div className="min-w-0">
              <div className="text-[#B28F4B] text-lg lg:text-xl font-bold font-heading">GRUPO SOEASY</div>
              <div className="text-white/60 text-[11px] uppercase tracking-widest font-medium">Sistema de Gestão de Orçamento</div>
              <div className="text-[#B28F4B]/70 text-[10px] font-mono mt-0.5 truncate max-w-[380px]">{cctInfo}</div>
            </div>
          </div>

          {/* KPIs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0">
            <Kpi label="NF Total" value={`R$ ${fmt(dash.nf_total)}`} color="#B28F4B"/>
            <Kpi label="Lucro" value={`R$ ${fmt(dash.lucro)}`} color="#34D399"/>
            <Kpi label="Líquido" value={`R$ ${fmt(dash.liquido)}`} color="#22D3EE"/>
            <Kpi label="Postos" value={dash.postos} color="#A78BFA"/>
            {dash.cct_ativa ? (
              <div className="shrink-0 bg-emerald-500/15 border border-emerald-500/40 rounded-lg px-3 py-2 min-w-[80px]">
                <div className="text-emerald-400 text-[9px] font-bold uppercase tracking-widest">Vigente</div>
                <div className="text-emerald-300 text-xs font-bold mt-0.5">{dash.dias_vigencia ?? 0} dias</div>
              </div>
            ) : null}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <button className="px-3 py-2 rounded-lg bg-[#0A0B1A]/60 border border-[#B28F4B]/25 text-[#A1A4C7] hover:text-white text-xs font-bold uppercase tracking-widest flex items-center gap-1.5">
              <Settings size={13}/> Config.
            </button>
            <button onClick={() => navigate("/setores")} className="px-3 py-2 rounded-lg bg-[#0A0B1A]/60 border border-[#B28F4B]/25 text-[#A1A4C7] hover:text-white text-xs font-bold uppercase tracking-widest flex items-center gap-1.5">
              <ArrowLeft size={13}/> Setores
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} className="px-3 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5">
              <LogOut size={13}/> Sair
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* SIDEBAR módulos SGO */}
        <aside className="hidden md:flex w-64 flex-col bg-[#0A0B1A] border-r border-[#B28F4B]/15 shrink-0">
          <div className="p-5 border-b border-[#B28F4B]/15">
            <div className="text-[#B28F4B] text-[10px] font-bold tracking-[0.25em] uppercase mb-1">SGO — Gestão de Orçamento</div>
            <div className="text-white/50 text-[10px]">Módulos</div>
          </div>
          <nav className="p-3 space-y-2 flex-1 overflow-y-auto">
            <div className="text-[9px] font-bold uppercase tracking-widest text-[#B28F4B]/70 px-2 mb-2 mt-1">Módulos</div>
            <SgoNavItem to="/sgo/clientes" icon={Users} title="Cadastro de Clientes" sub="Clientes e Contratos" active={activeModule === "clientes"}/>
            <SgoNavItem to="/sgo/orcamentos" icon={Calculator} title="Orçamento" sub="Planilhas e Propostas" active={activeModule === "orcamento" || activeModule?.startsWith("orc-")}/>

            <div className="text-[9px] font-bold uppercase tracking-widest text-[#B28F4B]/70 px-2 mb-2 mt-5">Guia Rápido — Seções</div>
            <SgoSubItem to="/sgo/orcamentos" label="Orçamentos" sub="Lista de orçamentos" active={activeModule === "orc-lista"}/>
            <SgoSubItem to="/sgo/orcamentos/novo?sec=contratante" label="Dados do Contratante" sub="Dados do cliente" active={activeModule === "orc-contratante"}/>
            <SgoSubItem to="/sgo/orcamentos/novo?sec=cct" label="CCT" sub="Convenção coletiva" active={activeModule === "orc-cct"}/>
            <SgoSubItem to="/sgo/orcamentos/novo?sec=postos" label="Postos" sub="Postos de trabalho" active={activeModule === "orc-postos"}/>
            <SgoSubItem to="/sgo/orcamentos/novo?sec=planilha" label="Planilha de Custos" sub="Cálculo e encargos" active={activeModule === "orc-planilha"}/>
            <SgoSubItem to="/sgo/orcamentos/novo?sec=proposta" label="Proposta Final" sub="Documento final" active={activeModule === "orc-proposta"}/>
          </nav>
          <div className="p-4 border-t border-[#B28F4B]/15">
            <div className="text-[9px] uppercase tracking-widest text-[#6B6E8F] font-bold">Logado como</div>
            <div className="text-white text-sm font-semibold mt-1 truncate">{user?.nome}</div>
            <div className="text-[#B28F4B] text-[10px] uppercase tracking-widest font-bold">{user?.nivel}</div>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          {children}
        </main>
      </div>

      {/* Footer NF */}
      <footer className="border-t border-[#B28F4B]/15 bg-[#0A0B1A] px-6 py-3 text-right">
        <span className="text-[#B28F4B] text-xs font-bold tracking-widest uppercase">
          NF: R$ {fmt(dash.nf_total)} · LÍQ: R$ {fmt(dash.liquido)}
        </span>
      </footer>
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div className="shrink-0 bg-[#0A0B1A]/60 border border-[#B28F4B]/20 rounded-lg px-3 py-2 min-w-[92px] text-center">
      <div className="font-mono font-bold text-sm" style={{ color }}>{value}</div>
      <div className="text-[9px] text-[#A1A4C7] uppercase tracking-widest font-semibold mt-0.5">{label}</div>
    </div>
  );
}

function SgoNavItem({ to, icon: Icon, title, sub, active }) {
  return (
    <Link
      to={to}
      data-testid={`sgo-nav-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className={`block p-4 rounded-xl border transition-all ${
        active
          ? "bg-[#1F2350] border-[#B28F4B] gold-glow"
          : "bg-[#131532]/60 border-[#B28F4B]/15 hover:border-[#B28F4B]/40 hover:bg-[#131532]"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className={active ? "text-[#B28F4B]" : "text-[#A1A4C7]"}/>
        <div className={`font-bold text-xs uppercase tracking-wider ${active ? "text-[#B28F4B]" : "text-white"}`}>{title}</div>
      </div>
      <div className="text-[10px] text-[#A1A4C7]">{sub}</div>
    </Link>
  );
}

function SgoSubItem({ to, label, sub, active }) {
  return (
    <Link
      to={to}
      className={`block px-3 py-2 rounded-lg text-xs border-l-2 transition ${
        active
          ? "border-[#B28F4B] bg-[#1F2350]/50 text-[#B28F4B]"
          : "border-transparent text-[#A1A4C7] hover:text-white hover:bg-[#131532]/60"
      }`}
    >
      <div className="font-bold uppercase tracking-wide">▸ {label}</div>
      <div className="text-[9px] text-[#6B6E8F] mt-0.5 ml-3">{sub}</div>
    </Link>
  );
}
